import { initializeApp } from "firebase/app";
import { isEmpty } from "lodash";
import placeholderImage from "../../assets/file-blank-solid-240.png";
import {
  getDatabase,
  ref,
  push,
  update,
  get,
  onValue,
  onChildChanged,
  off,
  remove,
  set,
  orderByKey,
  startAt,
  endAt,
  equalTo,
  query,
  limitToLast,
  runTransaction,
  orderByChild,
} from "firebase/database";
import {
  getAuth,
  OAuthProvider,
  GoogleAuthProvider,
  SAMLAuthProvider,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendSignInLinkToEmail,
  signInAnonymously,
  signInWithPopup,
  isSignInWithEmailLink,
  signInWithCustomToken,
  signInWithEmailLink,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  validatePassword,
  signOut,
  getIdToken,
  reload,
} from "firebase/auth";
import { connectFunctionsEmulator, getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, list, deleteObject, uploadBytes, ref as storageRef, getDownloadURL } from "firebase/storage";
import { getFirestore } from "firebase/firestore";

import * as Sentry from "@sentry/react";
import { VendorMicrosoft } from "@styled-icons/typicons/VendorMicrosoft";
import { Google } from "@styled-icons/boxicons-logos/Google";
import { LockOpen } from "@styled-icons/boxicons-solid/LockOpen";
import { getPublicUrl } from "./storage";
import { clearWeakPassword } from "../../helpFunctions/weakPassword";

var config = {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_REALTIME_DB,
  projectId: import.meta.env.VITE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_ID,
};

const fbPath = (...path) => {
  if (!path) throw new Error("Empty path");
  let joined = path.join("/");
  for (let p of path)
    if (p === null || p === undefined || p === "") throw new Error(`Empty component in Firebase path ${joined}`);
  return joined;
};

const sizeOf = obj => {
  if (!obj) return 0;
  // TODO apply overhead coefficient
  return JSON.stringify(obj).length;
};

const replaceStackWithCallSite = (error, callSiteError) => {
  if (!(error instanceof Error) || !callSiteError.stack) return callSiteError;

  const callSiteFrames = callSiteError.stack.split("\n").slice(1).join("\n");
  try {
    error.stack = `${error.name}: ${error.message}\n${callSiteFrames}`;
    return error;
  } catch {
    // Some browsers may expose a read-only stack. The diagnostic error still has the application call site.
    callSiteError.cause = error;
    return callSiteError;
  }
};

export const callWithCloudFunctionErrorReporting = async (functionName, call) => {
  // Firebase creates rejected errors while decoding the response, which normally hides the application call site.
  const callSiteError = new Error(`Cloud Function failed: ${functionName}`);
  try {
    return await call();
  } catch (error) {
    const firebaseErrorCode = error?.code || "unknown";
    const diagnosticError = replaceStackWithCallSite(error, callSiteError);

    Sentry.withScope(scope => {
      scope.setTag("cloudFunction", functionName);
      scope.setTag("firebaseErrorCode", firebaseErrorCode);
      scope.setTag("httpStatus", /\[(\d+)\]$/.exec(error?.message ?? "")?.[1] ?? "none");
      scope.setFingerprint(["cloud-function", functionName, firebaseErrorCode]);
      scope.setContext("cloudFunctionCall", {
        functionName,
        online: typeof navigator === "undefined" ? "unknown" : navigator.onLine,
        visibilityState: typeof document === "undefined" ? "unknown" : document.visibilityState,
      });
      scope.captureException(diagnosticError);
    });

    // Preserve FirebaseError identity, code and details for the caller's handling.
    throw error;
  }
};

class Firebase {
  constructor() {
    // Playwright route-level tests can inject a deterministic Firebase replacement
    // before the app boots. Keep this DEV-only so production and built artifacts
    // always use the real Firebase SDK.
    if (import.meta.env.DEV && typeof window !== "undefined" && window.__fictiveE2E?.firebase) {
      return window.__fictiveE2E.firebase;
    }

    let fireApp = initializeApp(config);
    this.auth = getAuth(fireApp);
    this.db = getDatabase(fireApp);
    this.store = getFirestore(fireApp);
    this.storage = getStorage(fireApp);
    this.functions = getFunctions(fireApp, import.meta.env.VITE_REGION);

    // Local dev only: route Cloud Functions calls to the local Firebase emulator, so
    // node-functions changes can be tested in the browser before deploy. Auth and the
    // database still use the configured project (e.g. fictive-dev). Enable with
    // VITE_FUNCTIONS_EMULATOR=1 (matched explicitly, so a stray =0/"false" can't enable
    // it). Never set in built/deployed environments.
    if (["1", "true"].includes(import.meta.env.VITE_FUNCTIONS_EMULATOR)) {
      connectFunctionsEmulator(this.functions, "localhost", 5001);
    }

    this.authProviders = {
      microsoft: {
        auth: new OAuthProvider("microsoft.com"),
        displayName: "Microsoft",
        icon: VendorMicrosoft,
        showInSignin: true,
      },
      google: {
        auth: new GoogleAuthProvider(),
        displayName: "Google",
        icon: Google,
        showInSignin: true,
      },
      "saml.fictive-entra-test": {
        auth: new SAMLAuthProvider("saml.fictive-entra-test"),
        displayName: "Entra SAML Test",
        icon: LockOpen,
        showInSignin: false,
        logoutRedirect: true,
      },
      "oidc.fictive-entra-test": {
        auth: new OAuthProvider("oidc.fictive-entra-test"),
        displayName: "Entra OIDC Test",
        icon: LockOpen,
        showInSignin: false,
        logoutRedirect: true,
      },
      anon: {
        auth: null,
        displayName: "Anonymous",
        icon: LockOpen,
        emailMatch: null,
        showInSignin: false,
        logoutRedirect: true,
      },
    };

    // NOTE this snippet is for debugging, if we need to force choice of account
    // It's currently not used, because it's deemed that most users would prefer a fast login flow
    // this.authProviders.google.auth.setCustomParameters({
    //   prompt: "select_account",
    // });

    // Prepare invocable cloud functions
    this.copyScenario = httpsCallable(this.functions, "assets-copyScenario");
    this.translateTexts = httpsCallable(this.functions, "assets-translateTexts");
    this.registerUser = httpsCallable(this.functions, "auth-registerUser");
    this.getUserDetails = httpsCallable(this.functions, "auth-getUserDetails");
    this.ensureSignedSessionUrls = httpsCallable(this.functions, "auth-ensureSignedSessionUrls");
    this.createInvite = httpsCallable(this.functions, "auth-createInvite");
  }

  // Cloud functions

  doCopyScenario = inputObj => this.copyScenario(inputObj);

  doTranslateTexts = inputObj => this.translateTexts(inputObj);

  doRegisterUser = data => this.registerUser(data);

  doGetUserDetails = uid => this.getUserDetails({ uid });

  // Login user
  doSignInWithEmailAndPassword = (email, password) => signInWithEmailAndPassword(this.auth, email, password);

  doSendEmailVerification = () => sendEmailVerification(this.auth.currentUser);

  doSendSignInLinkToEmail = (email, actionCodeSettings) => sendSignInLinkToEmail(this.auth, email, actionCodeSettings);

  doSignInAnonymously = () => signInAnonymously(this.auth);

  doSignInWithProvider = provider =>
    this.authProviders[provider]
      ? signInWithPopup(this.auth, this.authProviders[provider].auth)
      : new Error(`Provider ${provider} not configured`);

  isSignInWithEmailLink = location => isSignInWithEmailLink(this.auth, location);

  doSignInWithCustomToken = token => signInWithCustomToken(this.auth, token);

  doSignInWithEmailLink = (email, location) => signInWithEmailLink(this.auth, email, location);

  signOut = (navigate, redirectTo = "/signin") => {
    window.localStorage.clear();
    clearWeakPassword();
    if (!this.auth.currentUser) {
      if (typeof navigate === "function") {
        return Promise.resolve(navigate(redirectTo, { replace: true, state: null }));
      }
      return Promise.resolve(navigate?.push({ pathname: redirectTo, state: null }));
    }
    return this.auth.currentUser.getIdTokenResult().then(res => {
      const provider = res.signInProvider;
      const nameId = this.auth.currentUser.email;
      if (provider && nameId && this.authProviders[provider]?.logoutRedirect) {
        return signOut(this.auth).then(() =>
          window.location.replace(
            `/signout?redirect=true&idp=${encodeURIComponent(provider)}&nameId=${encodeURIComponent(nameId)}`
          )
        );
      } else {
        // By loading the URL from scratch, we will clear all Firebase listeners and get a clean state
        // Otherwise we would need to find and clean out all listeners to Firebase data that may depend on authUser to be permitted
        // This also ensures that the DataStore is reset, currently haven't found a clean way to do this by listening to authUser becoming null
        return signOut(this.auth).then(() => window.location.replace(redirectTo));
        // firebase.doSignOut(); // This doesn't create a visual reload of the page but has above issues
      }
    });
  };

  doPasswordReset = (email, actionCodeSettings) => sendPasswordResetEmail(this.auth, email, actionCodeSettings);

  doPasswordUpdate = password => updatePassword(this.auth.currentUser, password);

  // Updating the password requires a recent sign-in
  doReauthenticateWithPassword = password =>
    reauthenticateWithCredential(
      this.auth.currentUser,
      EmailAuthProvider.credential(this.auth.currentUser.email, password)
    );

  // Checks a password against the Firebase Auth password policy, see PasswordPolicy component
  doValidatePassword = password => validatePassword(this.auth, password);

  // Other Auth

  authenticatedFetch = (url, options = {}) => {
    return this.getIdToken().then(token => {
      if (!options.headers) options.headers = {};
      options.headers["Authorization"] = `Bearer ${token}`;
      return fetch(url, options);
    });
  };

  getIdToken = () => getIdToken(this.auth.currentUser, false);

  refreshToken = () => getIdToken(this.auth.currentUser, true);

  reloadUser = () => reload(this.auth.currentUser);

  onAuthUserListener = (loginCb, logoutCb) =>
    this.auth.onAuthStateChanged(async authUser => {
      if (!authUser)
        // logged out
        return logoutCb();

      let claims = {};
      try {
        const idTokenResult = await authUser.getIdTokenResult();
        claims = idTokenResult?.claims || {};
      } catch (err) {
        console.error(err);
        return logoutCb();
      }

      let userId = authUser.uid;
      let dbUser = null;
      try {
        dbUser = await this.fetch(fbPath("users", userId));
      } catch (err) {
        // Ignore errors here as we have fallback strategy
        console.error(`Failed to read user profile for ${userId}`, err);
      }

      // No user found in db
      if (!dbUser) {
        dbUser = {
          email: authUser.email,
          displayName: (authUser.email && authUser.email.match(/^([^@]*)@/)[1]) || "Anonymous",
          termsConsent: false,
        };
      }

      loginCb({
        ...dbUser,
        userId,
        claims,
        providerData: authUser.providerData,
        refreshToken: authUser.refreshToken,
      });
    });

  // General Storage

  putFile = (path, file, metadata) => uploadBytes(storageRef(this.storage, path), file, metadata);

  deleteFile = path => deleteObject(storageRef(this.storage, path));

  listFiles = path => list(storageRef(this.storage, path));

  /**
   * Creates sources from Google Storage paths for use with FileLibrary component
   *
   * @param {object} opts
   * @param {string} opts.path in Google Storage
   * @param {string} opts.name for the source
   * @param {string} opts.thumbnailSize - Size of thumbnail to use
   * @param {boolean} opts.upload - Whether to enable upload
   * @returns {object}
   */
  createStorageSource = ({
    path,
    name,
    thumbnailSize = "320px",
    upload = false,
    canDelete = false,
    shared = false,
  }) => {
    if (!path || !name) {
      console.error("Invalid parameters provided to createStorageSource");
      return null;
    }
    const source = {
      name,
      shared,
      getFiles: async () => {
        try {
          const listResult = await this.listFiles(path);

          // Separate regular files from thumbnail files
          const regularFiles = [];
          const thumbnailMap = {};

          const thumbnailRegex = /\.resized\.(\d+px)\.webp$/;

          listResult.items.forEach(item => {
            const m = item.name.match(thumbnailRegex);
            if (m) {
              if (m[1] === thumbnailSize) {
                const originalName = item.name.replace(thumbnailRegex, "");

                // Store by original name for lookup
                if (!thumbnailMap[originalName]) {
                  thumbnailMap[originalName] = item;
                }
              }
            } else {
              regularFiles.push(item);
            }
          });

          const filePromises = regularFiles.map(async item => {
            let downloadUrl = getPublicUrl(item.fullPath);
            // We know that some paths are publicly accessible, so we can use them directly instead of making lots of API calls
            if (!downloadUrl) downloadUrl = await getDownloadURL(item);

            let thumbnailUrl = getPublicUrl(thumbnailMap[item.name]?.fullPath);
            if (!thumbnailUrl && thumbnailMap[item.name]) thumbnailUrl = await getDownloadURL(thumbnailMap[item.name]);

            // Use dedicated thumbnail if available, otherwise fallback to current logic
            thumbnailUrl =
              thumbnailUrl ||
              (item.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) || item.fullPath.match(/\/media\//)
                ? downloadUrl
                : placeholderImage);

            // TODO we could return metadata but that requires a extra fetch with getMetadata
            return {
              id: item.fullPath,
              name: item.name,
              path: item.fullPath,
              thumbnail: thumbnailUrl,
              url: downloadUrl,
            };
          });

          return Promise.all(filePromises);
        } catch (error) {
          console.error(`Error fetching files from ${path}:`, error);
          return [];
        }
      },
    };
    if (upload) {
      source.uploadFile = (name, data, metadata) => this.putFile(fbPath(path, name), data, metadata);
    }
    if (canDelete) {
      source.deleteFile = async filePath => {
        await this.deleteFile(filePath);
        // Also delete any generated thumbnails for this file
        try {
          const listResult = await this.listFiles(path);
          const fileName = filePath.split("/").pop();
          const thumbsToDelete = listResult.items.filter(
            item => item.name.startsWith(fileName + ".resized.") && item.name.endsWith(".webp")
          );
          await Promise.all(thumbsToDelete.map(item => this.deleteFile(item.fullPath)));
        } catch (e) {
          // Thumbnail cleanup is best-effort; the primary file is already deleted
          console.warn("Failed to clean up thumbnails:", e);
        }
      };
    }
    return source;
  };

  // General DB

  /**
   * Fetches data (once) from Firebase Realtime Database while recording metrics to Sentry.
   * @param {string} path
   * @param {import("firebase/database").QueryConstraint[]} queries
   * @returns {Promise<object>}
   */
  instrumentedFetch = (path, queries) => {
    return Sentry.startSpan({ op: "db", name: `once: ${path}` }, async span => {
      let data = await this.fetch(path, queries);
      span.setAttribute("bytes", sizeOf(data));
      return data;
    });
  };

  /**
   * Fetches (once) data from Firebase Realtime Database.
   * @param {string} path
   * @param {import("firebase/database").QueryConstraint[]} queries
   * @returns {Promise<object>}
   */
  fetch = async (path, queries) => {
    let dbRef = ref(this.db, path);
    if (queries) {
      dbRef = query(dbRef, ...queries);
    }
    const snapshot = await get(dbRef);
    return snapshot.val();
  };

  instrumentedListen = (path, callback, queries) => {
    if (!callback) return this.listen(path, null);
    let span = Sentry.startInactiveSpan({ op: "db", name: `on: ${path}` });
    let first = false;
    return this.listen(
      path,
      data => {
        // We only measure the span from start to first retun of data
        // NOTE that we might fail to end the span if we turn off the listen before it returns any
        if (!first) {
          first = true;
          span.end();
          // NOTE this will not instrument bytes correctly, because the callback will contain all
          // data at the location but the Websocket has only fetched the changed data
          span.setAttribute("bytes", sizeOf(data));
        }
        callback(data);
      },
      queries
    );
  };

  listen = (path, callback, queries) => {
    let dbRef = ref(this.db, path);
    if (queries) {
      dbRef = query(dbRef, ...queries);
    }
    if (callback) {
      return onValue(dbRef, snapshot => {
        callback(snapshot.val());
      });
    } else {
      // NOTE not always recommended as it clears all listeners on this ref (but not children)
      return off(dbRef);
    }
  };

  listenList = (path, callback, queries) => {
    let dbRef = ref(this.db, path);
    if (queries) {
      dbRef = query(dbRef, ...queries);
    }
    if (callback) {
      onChildChanged(dbRef, snapshot => {
        callback(snapshot.val());
      });
    } else {
      // NOTE not always recommended as it clears all listeners on this ref (but not children)
      off(dbRef);
    }
    return this.fetch(path, queries);
  };

  instrumentedUpdate = (path, value) =>
    Sentry.startSpan({ op: "db", name: `update: ${path}` }, span => this.update(path, value));

  update = (path, value) => {
    if (value === null) {
      return remove(ref(this.db, path));
    } else if (typeof value !== "object" || Array.isArray(value)) {
      throw new Error("Update requires an object as value");
    } else if (isEmpty(value)) {
      throw new Error("Cannot update with an empty object");
    } else {
      return update(ref(this.db, path), value);
    }
  };

  // Scenario

  scenarioCoverImages = () => list(storageRef(this.storage, "thumbs/scenarioCoverImages"));

  scenarioSlideRef = (scenarioId, fileName) =>
    storageRef(this.storage, fbPath("scenarios", scenarioId, "slides", fileName));

  scenarioDocRef = (scenarioId, fileName) =>
    storageRef(this.storage, fbPath("scenarios", scenarioId, "docs", fileName));

  scenarioSlidePut = (scenarioId, fileName, file) => uploadBytes(this.scenarioSlideRef(scenarioId, fileName), file);

  scenarioSlideDelete = (scenarioId, fileName) => deleteObject(this.scenarioSlideRef(scenarioId, fileName));

  scenarioDocPut = (scenarioId, fileName, file) => uploadBytes(this.scenarioDocRef(scenarioId, fileName), file);

  scenarioDocDelete = (scenarioId, fileName) => deleteObject(this.scenarioDocRef(scenarioId, fileName));

  fetchAllScenarios = () => this.instrumentedFetch("/scenarios");

  fetchScenariosBetweenIds = ids => {
    if (!ids || !ids.length) return Promise.resolve({});
    return this.instrumentedFetch("/scenarios", [orderByKey(), startAt(ids[0]), endAt(ids[ids.length - 1])]);
  };

  fetchScenario = id => this.instrumentedFetch(fbPath("scenarios", id));

  updateScenario = (id, value) => this.update(fbPath("scenarios", id), value);

  updateManyScenarios = updates => this.update(fbPath("scenarios"), updates);

  pushNewSuggestion = suggestionObject => push(ref(this.db, fbPath("suggestions")), suggestionObject);

  updateSuggestions = updateObject => this.update(fbPath("suggestions"), updateObject);

  updateDocument = (scenarioId, docId, value) =>
    this.update(fbPath("scenarios", scenarioId, "documents", docId), value);

  pushNewDocument = (scenarioId, newDocument) =>
    push(ref(this.db, fbPath("scenarios", scenarioId, "documents")), newDocument);

  listenScenario = (id, callback) => this.listen(fbPath("scenarios", id), callback);

  updateScenarioModeExchange = (scenarioId, mode, exchangeId, value) =>
    this.update(fbPath("scenarios", scenarioId, "modes", mode, "exchanges", exchangeId), value);

  updateScenarioModeExchangeLine = (scenarioId, mode, exchangeId, lineId, value) =>
    this.update(fbPath("scenarios", scenarioId, "modes", mode, "exchanges", exchangeId, "lines", lineId), value);

  updateScenarioModeExchanges = (scenarioId, mode, value) =>
    this.update(fbPath("scenarios", scenarioId, "modes", mode, "exchanges"), value);

  // Sessions

  listenPersonalSessions = (userId, callback) =>
    this.instrumentedListen(fbPath("sessions"), callback, [orderByChild("playerId"), equalTo(userId), limitToLast(3)]);

  listenAllSessionsFromDate = (date, callback) =>
    this.instrumentedListen(fbPath("sessions"), callback, [orderByChild("created"), startAt(date), limitToLast(1000)]);

  stopListenSessions = () => this.listen("/sessions", null);

  listenSession = (sessionId, callback) => this.instrumentedListen(fbPath("sessions", sessionId), callback);

  updateSession = (sessionId, value) => this.update(fbPath("sessions", sessionId), value);

  setSessionSharedWith = (sessionId, value) => set(ref(this.db, fbPath("sessions", sessionId, "sharedWith")), value);

  updateSessionRating = (sessionId, evaluationId, sourceId, value) =>
    this.update(fbPath("sessions", sessionId, "evaluation", evaluationId, "ratings", sourceId), value);

  updateLineRating = (sessionId, lineId, evaluationId, sourceId, value) => {
    return this.update(
      fbPath("sessions", sessionId, "events", lineId, "evaluation", evaluationId, "ratings", sourceId),
      value
    );
  };

  addEventToSession = (sessionId, event) => {
    const appendSessionEventTransactor = eventToAdd => {
      return sessionEvents => {
        if (!sessionEvents) {
          return [eventToAdd];
        } else {
          return [...sessionEvents, eventToAdd];
        }
      };
    };
    const dbRef = ref(this.db, fbPath("sessions", sessionId, "events"));
    return runTransaction(dbRef, appendSessionEventTransactor(event));
  };

  // Organizations

  updateOrganizations = value => this.update("/organizations", value);

  fetchOrganizations = () => this.instrumentedFetch("/organizations");

  fetchOrganization = id => (id ? this.instrumentedFetch(fbPath("organizations", id)) : null);

  fetchOrganizationCoverImage = id => (id ? this.instrumentedFetch(fbPath("organizations", id, "coverImage")) : null);

  updateOrganization = (id, value) => this.update(fbPath("organizations", id), value);

  getSpecificOrganizations = orgIds => {
    if (!orgIds || !orgIds.length) throw new Error("No organization ids provided");
    return Sentry.startSpan({ op: "db", name: `once: <org>/${orgIds.join(", ")}` }, async span => {
      let promises = orgIds.map(id => this.fetch(fbPath("organizations", id)).then(org => ({ [id]: org })));
      let data = await Promise.all(promises).then(orgs => Object.assign({}, ...orgs));
      span.setAttribute("bytes", sizeOf(data));
      return data;
    });
  };

  // Users

  getUsersInOrganization = organizationId =>
    this.instrumentedFetch(fbPath("users"), [orderByChild("organization"), equalTo(organizationId)]);

  listenUser = (uid, callback) => this.instrumentedListen(fbPath("users", uid), callback);

  updateUser = (id, value) => this.update(fbPath("users", id), value);

  updateUserScenarioStats = (userId, scenarioId, value) =>
    this.update(fbPath("users", userId, "scenarioUserStats", scenarioId), value);

  // Voices, etc

  fetchAllVoices = () => this.instrumentedFetch("/voices");

  updateVoice = (id, value) => this.update(fbPath("voices", id), value);

  // Invites

  fetchInvite = id => this.fetch(fbPath("invites", id));

  fetchInvites = async (organizationId, scenarioId) => {
    let inviteIdsPath;
    if (scenarioId) {
      inviteIdsPath = fbPath("scenarios", scenarioId, "settings", "invites");
    } else if (organizationId) {
      // Org saves invites under root, not in settings
      inviteIdsPath = fbPath("organizations", organizationId, "invites");
    } else {
      return {};
    }
    const inviteIdMap = await this.fetch(inviteIdsPath);
    if (!inviteIdMap) return {};
    const inviteIds = Object.keys(inviteIdMap);
    if (inviteIds.length === 0) return {};
    const invitesArr = await Promise.all(inviteIds.map(id => this.fetchInvite(id).then(invite => ({ id, ...invite }))));
    // Filter out nulls (deleted invites)
    // TODO this is a bit broken, a null invite will still be an object with id and undefined fields
    // But the InviteList currently relies on this to display the invite as removable
    return Object.fromEntries(invitesArr.filter(invite => invite && invite.id).map(invite => [invite.id, invite]));
  };

  /**
   * Deletes an invite and removes its ID from scenario or organization settings.
   * @param {string} id - Invite ID
   * @param {string} organizationId
   * @param {string} scenarioId
   */
  async deleteInvite(id, organizationId, scenarioId) {
    await remove(ref(this.db, fbPath("invites", id)));
    // Remove invite ID from scenario/org settings
    if (scenarioId) {
      await this.update(fbPath("scenarios", scenarioId, "settings", "invites", id), null);
    } else if (organizationId) {
      // Org saves invites under root, not in settings
      await this.update(fbPath("organizations", organizationId, "invites", id), null);
    }
  }

  /**
   * Creates an invite and adds its ID to the scenario or organization settings.
   * @param {object} data
   * @param {string} opts.organization
   * @param {string} opts.scenario
   * @returns {Promise<string>} Invite ID
   */
  async doCreateInvite(data) {
    if (!data.organization && !data.scenario) {
      throw new Error("Either organizationId or scenarioId must be provided");
    }
    const key = await this.createInvite(data).then(result => result.data);
    if (!key) throw new Error("No key returned from invite creation");
    // Add invite ID to scenario/org settings
    if (data.scenario) {
      await set(ref(this.db, fbPath("scenarios", data.scenario, "settings", "invites", key)), true);
    } else if (data.organization) {
      // Org saves invites under root, not in settings
      await set(ref(this.db, fbPath("organizations", data.organization, "invites", key)), true);
    }
    return key;
  }
}

export default Firebase;
